import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [loginSuccessful, setLoginSuccessful] = useState(false);

  useEffect(() => {
    // Navigate to dashboard once user is set after successful login
    if (loginSuccessful && user) {
      navigate('/admin/dashboard');
    }
  }, [user, loginSuccessful, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Login successful!');
      setLoginSuccessful(true);
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1219] relative overflow-hidden flex items-center justify-center">
      <div className="scanline-overlay" />

      {/* Glow Effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        whileHover={{ x: -4 }}
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-10 flex items-center gap-2 text-primary hover:text-white transition-colors"
        data-testid="back-home-button"
      >
        <ArrowLeft />
        <span className="font-heading tracking-wider uppercase">Home</span>
      </motion.button>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md mx-6"
      >
        <div className="bg-card border border-border/50 p-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 bg-primary" />
              <h1 className="text-3xl font-heading font-bold tracking-wider text-primary">GRINDNATION</h1>
            </div>
            <h2 className="text-2xl font-heading font-bold text-white tracking-wider mb-2">ADMIN ACCESS</h2>
            <p className="text-gray-400 text-sm">Enter your credentials to continue</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="admin-login-form">
            <div className="mb-6">
              <label htmlFor="email" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">Email</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="Enter email"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="email-input"
              />
            </div>

            <div className="mb-8">
              <label htmlFor="password" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">Password</label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="Enter password"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="password-input"
              />
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full group relative px-8 py-4 bg-primary text-black font-heading font-bold text-lg tracking-widest uppercase overflow-hidden disabled:opacity-50"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 95% 100%, 0 100%)' }}
              data-testid="login-submit-button"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {loading ? 'AUTHENTICATING...' : 'LOGIN'}
                {!loading && <LogIn className="group-hover:translate-x-1 transition-transform" size={20} />}
              </span>
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
            </motion.button>
          </form>
        </div>

        {/* Corner Accent */}
        <div className="absolute top-0 right-0 w-2 h-2 bg-primary" />
      </motion.div>
    </div>
  );
};

export default AdminLogin;